import { useState, useEffect } from "react";

import {
  Button,
  Label,
  Modal,
  TextArea,
  TextField,
  Typography,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as DeleteRequestIcon } from "@king-ict/eupisi-ui/components/Icon/DeleteRequest.svg";
import { KindergartenRoleEnum } from "@king-ict/eupisi-ui/models";
import {
  hrDateWithTime,
  isEmpty,
  getStatusColor,
} from "@king-ict/eupisi-ui/utils";
import qs from "qs";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router";

import {
  getApplicantParentByRequestId as getApplicantParentByRequestIdApi,
  getOtherParentByRequestId as getOtherParentByRequestIdApi,
  getChildByRequestId as getChildByRequestIdApi,
  getLocationProgramsByRequestId as getLocationProgramsByRequestIdApi,
  getDraftRequest as getDraftRequestApi,
  getRequestCriterionDataByRequestId as getCriteriaByRequestIdApi,
  getInitialConversationByRequestId as getInitialConversationByRequestIdApi,
  getUnlockReasonsByRequestId as getUnlockReasonsByRequestIdApi,
  getRequestHistory as getRequestHistoryApi,
} from "../../../api/kindergartenApi";
import {
  useDeleteDraftRequestMutation,
  useDeleteRequestMutation,
} from "../../../api/requestApi";

import { useTypedSelector } from "../../../app/rootReducer";
import { useCurrentUser } from "../../../hooks/authorization/useCurrentUser";
import { useBusinessRule } from "../../../hooks/businessRule/useBusinessRule";
import EnrollmentTypeEnum from "../../../models/EnrollmentType/EnrollmentTypeEnum";
import InstitutionProgram from "../../../models/Institution/InstitutionProgram";
import ApplicantParent from "../../../models/Person/ApplicantParent";
import Child from "../../../models/Person/Child";
import OtherParent from "../../../models/Person/OtherParent";
import ParentNumberEnum from "../../../models/Person/ParentNumberEnum";
import InitialConversationQuestionGroup from "../../../models/Request/InitialConversationQuestionGroup";
import RequestBasicInfo from "../../../models/Request/RequestBasicInfo";
import RequestComment from "../../../models/Request/RequestComment";
import RequestCriterion from "../../../models/Request/RequestCriterion";
import RequestHistoryItem from "../../../models/Request/RequestHistoryItem";
import RequestStatusEnum from "../../../models/Request/RequestStatusEnum";
import RequestTypeEnum from "../../../models/Request/RequestTypeEnum";
import {
  dashboard as dashboardRoute,
  editRequestUrl,
  editPreschoolRequestUrl,
  draftRequestUrl,
  draftPreschoolRequestUrl,
  draftRequestAsAdminUrl,
  draftPreschoolRequestAsAdminUrl,
  editRequestAsAdminUrl,
  editPreschoolRequestAsAdminUrl,
  adminRequestsUrl,
  adminSubsequentRequestsUrl,
  adminPreschoolRequestsUrl,
} from "../../../routes/routes";
import ApplicantParentDetails from "./ApplicantParentDetails";
import ChildDetails from "./ChildDetails";
import CriterionDetails from "./CriterionDetails";
import InitialConversationDetails from "./InitialConversationDetails";
import InstitutionProgramDetails from "./InstitutionProgramDetails";
import OtherParentDetails from "./OtherParentDetails";
import styles from "./RequestDetailsPage.module.scss";
import RequestHistory from "./RequestHistory";

interface Props {
  requestBasicInfo: RequestBasicInfo;
  history: any;
  onGetDraft: (basicInfo: RequestBasicInfo) => void;
}

export default function RequestDetailsPage({
  requestBasicInfo,
  history,
  onGetDraft,
}: Props) {
  const { guid } = useParams<{ guid: string }>();
  const location = useLocation();
  const params = qs.parse(location.search.substring(1));
  const { applicantParentOib } = params;

  const {
    isRequestDeletionAllowed,
    isRequestEditingAllowed,
  } = useBusinessRule();

  const user = useCurrentUser();
  const pedagogicalYear = useTypedSelector((state) => state.pedagogicalYear);

  const [applicantParent, setApplicantParent] = useState({} as ApplicantParent);
  const [otherParent, setOtherParent] = useState({} as OtherParent);
  const [child, setChild] = useState({} as Child);
  const [locationPrograms, setLocationPrograms] = useState(
    [] as InstitutionProgram[]
  );
  const [criteria, setCriteria] = useState([] as RequestCriterion[]);
  const [initialConversation, setInitialConversation] = useState(
    [] as InitialConversationQuestionGroup[]
  );
  const [requestHistory, setRequestHistory] = useState(
    [] as RequestHistoryItem[]
  );

  const [unlockReasons, setUnlockReasons] = useState<RequestComment[]>(
    [] as RequestComment[]
  );

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openRequestDeletedModal, setOpenRequestDeletedModal] = useState(false);
  const [
    deleteRequest,
    { isLoading: isDeletingSubmittedRequest },
  ] = useDeleteRequestMutation();
  const [
    deleteDraftRequest,
    { isLoading: isDeletingDraftRequest },
  ] = useDeleteDraftRequestMutation();
  const isDeletingRequest =
    isDeletingSubmittedRequest || isDeletingDraftRequest;

  const handleToggleConfirmModal = () => {
    setOpenConfirmModal((prevOpenConfirmModal) => !prevOpenConfirmModal);
  };

  const handleToggleRequestDeletedModal = () => {
    setOpenRequestDeletedModal(
      (prevOpenRequestDeletedModal) => !prevOpenRequestDeletedModal
    );
  };

  const handleEditDraft = (guid: string) => {
    if (user.roleId === KindergartenRoleEnum.Parent) {
      history.push(
        requestBasicInfo.requestTypeId === RequestTypeEnum.Preschool
          ? draftPreschoolRequestUrl(guid)
          : draftRequestUrl(guid)
      );
    } else {
      window.location.href =
        requestBasicInfo.requestTypeId === RequestTypeEnum.Preschool
          ? draftPreschoolRequestAsAdminUrl(guid, applicantParentOib + "")
          : draftRequestAsAdminUrl(guid, applicantParentOib + "");
    }
  };

  const handleEditRequest = (requestId: number) => {
    if (user.roleId === KindergartenRoleEnum.Parent) {
      history.push(
        requestBasicInfo.requestTypeId === RequestTypeEnum.Preschool
          ? editPreschoolRequestUrl(requestId)
          : editRequestUrl(requestId)
      );
    } else {
      window.location.href =
        requestBasicInfo.requestTypeId === RequestTypeEnum.Preschool
          ? editPreschoolRequestAsAdminUrl(requestId, applicantParentOib + "")
          : editRequestAsAdminUrl(requestId, applicantParentOib + "");
    }
  };

  const handleDeleteRequest = async (requestId: number) => {
    if (isDeletingRequest) {
      return;
    }

    try {
      await deleteRequest(requestId).unwrap();
      handleToggleConfirmModal();
      handleToggleRequestDeletedModal();
    } catch {
      // Global API interceptor shows request errors.
    }
  };

  const handleDeleteDraftRequest = async (guid: string) => {
    if (isDeletingRequest) {
      return;
    }

    try {
      await deleteDraftRequest(guid).unwrap();
      handleToggleConfirmModal();
      handleToggleRequestDeletedModal();
    } catch {
      // Global API interceptor shows request errors.
    }
  };

  const handleBackToDashboard = () => {
    handleToggleRequestDeletedModal();
    if (user.roleId === KindergartenRoleEnum.Parent) {
      history.push(dashboardRoute());
    } else {
      window.location.href =
        requestBasicInfo.requestTypeId === RequestTypeEnum.Preschool
          ? adminPreschoolRequestsUrl()
          : requestBasicInfo.enrollmentTypeId ===
            EnrollmentTypeEnum.SubsequentEnrollment
          ? adminSubsequentRequestsUrl()
          : adminRequestsUrl();
    }
  };

  const getApplicantParent = async () => {
    if (!applicantParent.id) {
      const applicantParent = await getApplicantParentByRequestIdApi(
        requestBasicInfo.id
      );
      setApplicantParent(applicantParent);
    }
  };

  const getOtherParent = async () => {
    if (!otherParent.id) {
      const otherParent = await getOtherParentByRequestIdApi(
        requestBasicInfo.id
      );
      setOtherParent(otherParent);
    }
  };

  useEffect(() => {
    (async () => {
      if (guid && isEmpty(requestBasicInfo)) {
        const draftRequest = await getDraftRequestApi(guid, false);
        setApplicantParent(draftRequest.applicantParent);
        if (draftRequest.otherParent) {
          setOtherParent(draftRequest.otherParent!);
        }
        setChild(draftRequest.child);
        setLocationPrograms(draftRequest.institutionPrograms);
        setCriteria(draftRequest.criterionData.criteria);
        setInitialConversation(draftRequest.initialConversationQuestionGroups);

        onGetDraft({
          uId: guid,
          applicantParentOib: draftRequest.applicantParent.oib,
          otherParentOib: draftRequest.otherParent?.oib,
          createdByAdmin: draftRequest.createdByAdmin,
          parentNumberId: draftRequest.parentNumberId,
          childName: `${draftRequest.child.firstName} ${draftRequest.child.lastName}`,
          statusId: draftRequest.status.id,
          statusName: draftRequest.status.name,
          pedagogicalYearId: pedagogicalYear.currentPedagogicalYear.id,
          createdByAppUserID: draftRequest.createdByAppUserID,
          importantDateId: draftRequest.importantDateId,
          requestTypeId: draftRequest.requestTypeId,
          enrollmentTypeId: draftRequest.enrollmentTypeId,
          monthId: draftRequest.monthId,
          parentNumber: draftRequest.parentNumber,
        } as RequestBasicInfo);
      }
    })();
  }, [
    guid,
    onGetDraft,
    pedagogicalYear.currentPedagogicalYear.id,
    requestBasicInfo,
  ]);

  useEffect(() => {
    (async () => {
      if (requestBasicInfo?.statusId === RequestStatusEnum.Unlocked) {
        const res = await getUnlockReasonsByRequestIdApi(requestBasicInfo.id);
        setUnlockReasons(res);
      }
    })();
  }, [requestBasicInfo, requestBasicInfo.statusId]);

  const getChild = async () => {
    if (!child.id) {
      const child = await getChildByRequestIdApi(requestBasicInfo.id);
      setChild(child);
    }
  };

  const getLocationPrograms = async () => {
    if (!locationPrograms.length) {
      const locationPrograms = await getLocationProgramsByRequestIdApi(
        requestBasicInfo.id
      );
      setLocationPrograms(locationPrograms);
    }
  };

  const getCriteria = async () => {
    if (!criteria.length) {
      const criteria = await getCriteriaByRequestIdApi(requestBasicInfo.id);
      setCriteria(criteria.criteria);
    }
  };

  const getInitialConversation = async () => {
    if (!initialConversation.length) {
      const initialConversation = await getInitialConversationByRequestIdApi(
        requestBasicInfo.id
      );
      setInitialConversation(initialConversation);
    }
  };

  const isRequestDraft = requestBasicInfo.statusId === RequestStatusEnum.Draft;
  const shouldSkipRequestHistoryFetch =
    requestBasicInfo.statusId === RequestStatusEnum.Created || isRequestDraft;

  const getRequestHistory = async () => {
    if (shouldSkipRequestHistoryFetch) return;

    const loadedRequestHistory = await getRequestHistoryApi(
      requestBasicInfo.id
    );
    setRequestHistory(loadedRequestHistory);
  };

  const renderUnlockReasons = () => {
    return unlockReasons.map((reason) => {
      return (
        <TextArea
          key={reason.id}
          value={reason.comment}
          label={hrDateWithTime(reason.dateCreated)}
          disabled
          readonly
          ariaLabel={hrDateWithTime(reason.dateCreated)}
        />
      );
    });
  };

  const { t } = useTranslation();

  const emptyValue = "-";

  return (
    <>
      <div className={styles.detailsHeader}>
        <div className={styles.requestStatus}>
          {!!requestBasicInfo.statusId && (
            <Label color={getStatusColor(requestBasicInfo.statusId)}>
              {requestBasicInfo.statusName}
            </Label>
          )}
        </div>
        <div className={styles.headerButtons}>
          {isRequestDeletionAllowed(requestBasicInfo) && (
            <Button outline onClick={handleToggleConfirmModal}>
              {requestBasicInfo.requestTypeId === RequestTypeEnum.Preschool
                ? t("requestDetails.deletePreschoolRequest")
                : t("requestDetails.deleteRequest")}
            </Button>
          )}

          {isRequestEditingAllowed(requestBasicInfo) && (
            <Button
              onClick={() =>
                isRequestDraft
                  ? handleEditDraft(requestBasicInfo.uId!)
                  : handleEditRequest(requestBasicInfo.id)
              }
            >
              {requestBasicInfo.requestTypeId === RequestTypeEnum.Preschool
                ? t("requestDetails.editPreschoolRequest")
                : t("requestDetails.editRequest")}
            </Button>
          )}
        </div>
      </div>

      <Modal
        type={"confirm"}
        title={t("requestDetails.deleteRequestConfirmModalTitle")}
        open={openConfirmModal}
        confirmText={t("requestDetails.deleteRequestConfirmModalYes")}
        cancelText={t("common.no")}
        onClose={handleToggleConfirmModal}
        onConfirm={() =>
          isRequestDraft
            ? handleDeleteDraftRequest(requestBasicInfo.uId!)
            : handleDeleteRequest(requestBasicInfo.id)
        }
      >
        <div>{t("requestDetails.deleteRequestConfirmModalMessage")}</div>
      </Modal>

      <Modal
        type={"message"}
        open={openRequestDeletedModal}
        confirmText={t("common.close")}
        onConfirm={handleBackToDashboard}
        onClose={handleBackToDashboard}
      >
        <div className={styles.modalMessageContent}>
          <IconWrapper color="noted-2">
            <DeleteRequestIcon className={styles.requestIcon} />
          </IconWrapper>
          <Typography variant="largeBold">
            {t("requestDetails.deleteRequestSuccessfullMessage")}
          </Typography>
        </div>
      </Modal>

      <Row className={styles.rowSpace}>
        <Col>
          <TextField
            type="text"
            label={t("requestDetails.requestUid")}
            value={requestBasicInfo.uId}
            plaintext
          />
        </Col>
        <Col>
          <TextField
            type="text"
            label={t("requestDetails.requestCode")}
            value={requestBasicInfo.code}
            plaintext
          />
        </Col>
        <Col>
          <TextField
            type="text"
            label={t("requestDetails.requestDate")}
            value={
              !isRequestDraft && requestBasicInfo.dateUpdated
                ? hrDateWithTime(requestBasicInfo.dateUpdated)
                : !isRequestDraft && requestBasicInfo.dateCreated
                ? hrDateWithTime(requestBasicInfo.dateCreated)
                : ""
            }
            plaintext
          />
        </Col>
      </Row>

      {requestBasicInfo &&
        requestBasicInfo.statusId === RequestStatusEnum.Unlocked &&
        !isEmpty(unlockReasons) && (
          <>
            <div className={styles.unlockReasons}>
              <Typography variant="largeBold" component="h1">
                {t("requestDetails.unlockReasonsHeading")}
              </Typography>
              {renderUnlockReasons()}
            </div>
            <hr className={styles.line} />
          </>
        )}

      <ApplicantParentDetails
        emptyValue={emptyValue}
        applicantParent={applicantParent}
        parentNumber={requestBasicInfo.parentNumber}
        onExpandablePanelClick={isRequestDraft ? undefined : getApplicantParent}
      />
      <hr className={styles.line} />

      {requestBasicInfo.parentNumberId === ParentNumberEnum.TwoParents && (
        <>
          <OtherParentDetails
            emptyValue={emptyValue}
            otherParent={otherParent}
            onExpandablePanelClick={isRequestDraft ? undefined : getOtherParent}
          />
          <hr className={styles.line} />
        </>
      )}

      <ChildDetails
        emptyValue={emptyValue}
        child={child}
        onExpandablePanelClick={isRequestDraft ? undefined : getChild}
      />
      <hr className={styles.line} />

      <InstitutionProgramDetails
        institutionPrograms={locationPrograms}
        onExpandablePanelClick={
          isRequestDraft ? undefined : getLocationPrograms
        }
      />
      <hr className={styles.line} />

      <CriterionDetails
        applicantOib={requestBasicInfo.applicantParentOib}
        otherParentOib={requestBasicInfo.otherParentOib}
        childOib={requestBasicInfo.childOib}
        criteria={criteria}
        onExpandablePanelClick={isRequestDraft ? undefined : getCriteria}
      />
      <hr className={styles.line} />
      <InitialConversationDetails
        emptyValue={emptyValue}
        questionGroups={initialConversation}
        onExpandablePanelClick={
          isRequestDraft ? undefined : getInitialConversation
        }
      />
      <hr className={styles.line} />
      <RequestHistory
        requestHistory={requestHistory}
        onExpandablePanelClick={
          shouldSkipRequestHistoryFetch ? undefined : getRequestHistory
        }
      />
      <hr className={styles.line} />
    </>
  );
}
